/* eslint-disable @next/next/no-img-element */
import {
  AseobiPaymentMetadataRequest,
  AseobiPaymentMetadataRequestAttire,
  Currency,
  GenerateAsoebiPaymentMetadataMutation,
  GenerateAsoebiPaymentMetadataMutationVariables,
  GetAseobiAttiresQuery,
  GetAseobiQuery,
  PaymentProcessor,
  UserQuery,
  UserQueryVariables,
} from "@src/@types/api.d";
import getSymbolFromCurrency from "currency-symbol-map";
import { Form, Formik, FormikProps } from "formik";
import React, { useRef, useState } from "react";
import * as Yup from "yup";
import Input from "../Input";
import InputPhone from "../InputPhone";
import styles from "./styles.module.scss";
import Icon from "../Icon";
import toast from "react-hot-toast";
import { useMutation, useQuery } from "@apollo/client";
import { GetProfileDetails } from "@src/graphql/queries";
import { useModalPaystackPayment } from "@src/hooks/useModalPaystackPayment";
import { PaystackProps } from "react-paystack/dist/types";
import { GenerateAsoebiPaymentMetadata } from "@src/graphql/mutations";
import Button from "../Button";
import AsoebiStripePayment from "./AsoebiStripePayment";

interface Props {
  asoebi: GetAseobiQuery | undefined;
  selectedAsoebiId: string | null;
  attires: GetAseobiAttiresQuery["getAseobiAttires"];
  quantities: {
    [key: string]: number;
  };
  finalTotal: number;
  totalQuantity: number;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  eventUuid: string | undefined;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

interface Values {
  name: string;
  phoneNumber: string;
  email: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
}

// Paystack Payment Component (for NGN)
const AsoebiPaystackPayment = ({
  asoebi,
  selectedAsoebiId,
  attires,
  quantities,
  finalTotal,
  totalQuantity,
  setStep,
  eventUuid,
  setOpen,
}: Props) => {
  // Define Yup schema for Paystack payment (NGN)
  const OrderFormSchema = Yup.object().shape({
    name: Yup.string()
      .min(2, "Name must be at least 2 characters")
      .max(100, "Name must be less than 100 characters")
      .required("Name is required"),
    // phoneNumber: Yup.string()
    //   .matches(
    //     /^\+?[1-9]\d{1,14}$/,
    //     "Please enter a valid phone number (e.g., +1234567890)"
    //   )
    //   .required("Phone number is required"),
    email: Yup.string()
      .email("Please enter a valid email address")
      .required("Email is required"),
    address: Yup.string()
      .min(5, "Address must be at least 5 characters")
      .required("Address is required"),
    city: Yup.string()
      .min(2, "City must be at least 2 characters")
      .required("City is required"),
    state: Yup.string()
      .min(2, "State must be at least 2 characters")
      .required("State is required"),
    zipCode: Yup.string()
      .min(3, "Zip code must be at least 3 characters")
      .required("Zip code is required"),
  });

  // Mutation for generating payment metadata
  const [generatePaymentMetadata] = useMutation<
    GenerateAsoebiPaymentMetadataMutation,
    GenerateAsoebiPaymentMetadataMutationVariables
  >(GenerateAsoebiPaymentMetadata, {
    // onCompleted(data) {
    //   toast.success("Payment metadata generated successfully");
    // },
    onError(error) {
      toast.error(
        <p className="font-inter">
          {error?.message ?? "Failed to generate payment metadata"}
        </p>
      );
    },
  });

  const { data: profile } = useQuery<UserQuery, UserQueryVariables>(
    GetProfileDetails
  );

  const [phoneNumber, setPhoneNumber] = useState("");

  const formikRef = useRef<FormikProps<Values>>(null);

  // Paystack configuration (initially empty, updated in handleSubmit)
  const [paystackConfig, setPaystackConfig] = useState<PaystackProps | null>(
    null
  );

  const onSuccess = () => {
    toast.success("Payment successful");
    // Optionally show <PaymentSuccess /> or redirect
  };
  const onClose = () => {
    toast.error("Payment window closed");
  };

  const handleSubmit = async (values: Values) => {
    if (!eventUuid || !selectedAsoebiId) {
      toast.error("Event ID or Asoebi ID is missing");
      return;
    }

    // Prepare attire data for mutation
    const attireInput: AseobiPaymentMetadataRequestAttire[] =
      attires
        ?.filter((item) => (quantities[item.id] || 0) > 0)
        .map((item) => ({
          aseobi_id: item.aseobi_id,
          attire_id: item.id,
          quantity: 1,
          yards: quantities[item.id],
        })) ?? [];

    // Prepare mutation input
    const input: AseobiPaymentMetadataRequest = {
      aseobi_id: selectedAsoebiId,
      attires: attireInput,
      contact_email: values.email,
      contact_phone: phoneNumber,
      currency: Currency.Ngn,
      delivery_address: values.address,
      delivery_city: values.city,
      delivery_state: values.state,
      delivery_zip_code: values.zipCode,
      note: "Asoebi order payment",
      payment_provider: PaymentProcessor.Paystack,
      user_id: profile?.user?.uuid ?? "",
    };

    try {
      // Call mutation to generate payment metadata
      const { data: mutationData } = await generatePaymentMetadata({
        variables: {
          event_id: eventUuid,
          input,
        },
        // onCompleted(data) {
        //   console.log(data);
        // },
      });

      const paymentMeta = mutationData?.generateAsoebiPaymentMetadata;

      setPaystackConfig({
        reference: paymentMeta?.payment_reference || "",
        email: paymentMeta?.contact_email || "",
        amount: (paymentMeta?.total_amount ?? 0) * 100 || 0,
        publicKey: process.env.NEXT_PUBLIC_PAYSTACK_PUBLIC_KEY || "",
        metadata: paymentMeta,
        // bearer: "all-proportional",
        // split: paymentMeta?.split_payments,
        split: {
          type: "percentage",
          bearer_type: "all-proportional",
          subaccounts: paymentMeta?.split_payments?.map((item) => ({
            subaccount: item.sub_account_code,
            share: item.split_percentage,
          })),
          // bearer_subaccount: paymentMeta?.split_payments?.filter(
          //   (item) => item.couples_account
          // )[0]?.sub_account_code,
          // subaccounts: [
          //   {
          //     subaccount_code:
          //       paymentMeta?.split_payments?.[0]?.sub_account_code,
          //     share: paymentMeta?.split_payments?.[0]?.split_percentage,
          //   },
          //   {
          //     subaccount_code:
          //       paymentMeta?.split_payments?.[1]?.sub_account_code,
          //     share: paymentMeta?.split_payments?.[1]?.split_percentage,
          //   },
          // ],
        },
        // subaccount: paymentMeta?.sub_account_code,
        // transaction_charge: Math.round(
        //   ((paymentMeta?.split_percentage ?? 0) / 100) *
        //     (finalTotal ?? 0) *
        //     100 || 0
        // ),
      });
    } catch (error) {
      toast.error("Failed to initiate payment");
      console.error(error);
    }
  };

  useModalPaystackPayment(paystackConfig, {
    onSuccess: () => {
      setPaystackConfig(null);
      onSuccess();
      setStep(7);
    },
    onClose: () => {
      setPaystackConfig(null);
      onClose();
    },
    onError: (error) => {
      setPaystackConfig(null);
      toast.error(
        error instanceof Error
          ? error.message
          : "Unable to open payment. Please try again."
      );
    },
  });

  return (
    <div className="flex flex-col flex-grow h-full min-h-0">
      <div className="mb-4 flex-shrink-0">
        <h2 className="mb-4 font-inter text-2xl font-semibold">
          {
            asoebi?.getAseobi?.find((item) => item.id === selectedAsoebiId)
              ?.name
          }
        </h2>
        <p className="text-sm text-gray-500 font-inter">Finish your order</p>
      </div>

      <div className="border border-gray-300 rounded-xl py-4 flex flex-col gap-4 justify-center items-center w-full mb-4 flex-shrink-0">
        <div className="flex">
          {attires
            ?.filter((item) => (quantities[item.id] || 0) > 0)
            .slice(0, 3)
            .map((item) => (
              <div key={item.id} className="w-14 h-14">
                {item.images && item.images[0] ? (
                  <img
                    src={item.images[0]}
                    alt={item.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-red-300"></div>
                )}
              </div>
            ))}
        </div>

        <p className="text-lg font-inter">
          {attires?.[0]?.currency_iso
            ? getSymbolFromCurrency(attires[0].currency_iso)
            : "NGN"}
          {finalTotal.toLocaleString()}
        </p>

        <p className="text-sm text-gray-500 font-inter">
          {totalQuantity} items, {totalQuantity} yards
        </p>
      </div>

      <Formik
        initialValues={{
          name: "",
          phoneNumber: "",
          email: "",
          address: "",
          city: "",
          state: "",
          zipCode: "",
        }}
        validationSchema={OrderFormSchema}
        onSubmit={handleSubmit}
        innerRef={formikRef}
      >
        {(props: FormikProps<Values>) => (
          <Form
            className={`${styles["form-container"]} flex flex-col h-full min-h-0`}
          >
            <div className="flex flex-col gap-4 flex-1 overflow-y-auto thin-scrollbar pr-2">
              <Input
                type="text"
                name="name"
                label="Name"
                placeholder="Type your full name"
              />
              <InputPhone
                name="phoneNumber"
                label="Phone number"
                placeholder="Type your phone number"
                value={phoneNumber}
                setValue={setPhoneNumber}
                className="font-inter"
              />
              <Input
                type="email"
                name="email"
                label="Email address"
                placeholder="Type your email address"
              />
              <Input
                type="text"
                name="address"
                label="Address"
                placeholder="Type your delivery address"
              />
              <div className="flex gap-2">
                <Input
                  type="text"
                  name="city"
                  label="City"
                  placeholder="Type your city"
                />
                <Input
                  type="text"
                  name="state"
                  label="State"
                  placeholder="Type your state"
                />
              </div>
              <Input
                type="text"
                name="zipCode"
                label="Zip Code"
                placeholder="Type your zip code"
              />
            </div>
            <div className="flex gap-2 flex-shrink-0 pt-4 border-t border-gray-200 mt-4">
              <button
                className="w-full py-2 border border-gray-500 hover:bg-gray-200 text-black font-inter rounded-md transition duration-300"
                type="button"
                onClick={() => setStep(2)}
              >
                Go back
              </button>
              <Button
                type="submit"
                label="Pay via Paystack"
                className="w-full py-2 px-1 hover:bg-[#34544F] bg-[#4e7e77] text-white font-inter rounded-md transition duration-300 flex items-center justify-center disabled:cursor-not-allowed disabled:bg-[#cccccc]"
                disabled={
                  !!paystackConfig ||
                  phoneNumber === "" ||
                  props.values.name === "" ||
                  props.values.email === "" ||
                  props.values.address === "" ||
                  props.values.city === "" ||
                  props.values.state === "" ||
                  props.values.zipCode === ""
                }
              >
                <div className="flex gap-2 items-center">
                  <span className="hidden xs:flex">
                    <Icon iconName="paystack" />
                  </span>
                  <p>Pay via Paystack</p>
                </div>
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

// Main AsoebiPayment wrapper component
const AsoebiPayment = (props: Props) => {
  if (props.attires?.some((attire) => attire.vendor !== "COUPLE")) {
    return (
      <p role="alert">
        This fabric is no longer available. Return to the group to choose
        another fabric.
      </p>
    );
  }
  // Detect currency from attires
  const currency =
    (props.attires?.[0]?.currency_iso?.toLowerCase() as Currency) ||
    Currency.Ngn;
  // const currency = Currency.usd;
  const isNairaCurrency = currency === Currency.Ngn;
  // const isNairaCurrency = false;

  // If currency is not Naira, use Stripe payment component
  if (!isNairaCurrency) {
    return <AsoebiStripePayment {...props} currency={currency} />;
  }

  // Use Paystack for Naira
  return <AsoebiPaystackPayment {...props} />;
};

export default AsoebiPayment;
